<script setup>
/**
 * Header fixe avec titre de page, actions utilisateur et navigation
 */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLayoutStore } from '@/stores/layout'
import { useNotificationsStore } from '@/stores/notifications'
import { apiFetch } from '@/services/api'

const router = useRouter()
const authStore = useAuthStore()
const layoutStore = useLayoutStore()
const notificationsStore = useNotificationsStore()

// ──────────────────────────────────────────────────────────────
// Dropdown utilisateur : ouverture au clic + fermeture au clic
// extérieur ou à la touche Échap. Un menu hover était instable
// à cause du gap entre le bouton et le dropdown.
// ──────────────────────────────────────────────────────────────
const userMenuOpen = ref(false)
const userMenuRef = ref(null)

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value
}
function closeUserMenu() {
  userMenuOpen.value = false
}

// Dropdown notifications
const notifOpen = ref(false)
const notifRef = ref(null)
function toggleNotif() {
  notifOpen.value = !notifOpen.value
}
function onNotifClick(n) {
  notificationsStore.markAsRead(n.id)
  if (n.action_url) {
    notifOpen.value = false
    if (/^https?:\/\//i.test(n.action_url)) window.open(n.action_url, '_blank')
    else router.push(n.action_url)
  }
}
function markAllNotif() {
  notificationsStore.markAllAsRead()
}
const notifications = computed(() => notificationsStore.notifications)
function notifTime(d) {
  if (!d) return ''
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function handleClickOutside(event) {
  if (userMenuOpen.value && userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    userMenuOpen.value = false
  }
  if (searchOpen.value && searchBoxRef.value && !searchBoxRef.value.contains(event.target)) {
    searchOpen.value = false
  }
  if (notifOpen.value && notifRef.value && !notifRef.value.contains(event.target)) {
    notifOpen.value = false
  }
}
function handleEscape(event) {
  if (event.key === 'Escape') {
    closeUserMenu()
    searchOpen.value = false
    notifOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
  notificationsStore.fetchNotifications()
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
})

const user = computed(() => authStore.user)
const userInitials = computed(() => {
  if (!user.value) return ''
  const first = user.value.firstname?.[0] || ''
  const last = user.value.lastname?.[0] || ''
  return (first + last).toUpperCase()
})

const unreadCount = computed(() => notificationsStore.unreadCount)

// ──────────────────────────────────────────────────────────────
// Recherche globale (omnibox) : suggestions en direct dans un
// dropdown, navigation au clic, Entrée → page de résultats /search.
// ──────────────────────────────────────────────────────────────
const isAdmin = computed(() => authStore.isAdmin)
const searchQuery = ref('')
const searchOpen = ref(false)
const searchLoading = ref(false)
const searchData = ref({ properties: [], contacts: [], agencies: [], agents: [] })
const searchCounts = ref({ properties: 0, contacts: 0, agencies: 0, agents: 0, total: 0 })
const searchBoxRef = ref(null)
let searchTimer = null
let searchReqId = 0

const hasSuggestions = computed(() => {
  const d = searchData.value
  return (
    d.properties.length || d.contacts.length || d.agencies.length || d.agents.length
  ) > 0
})

const formatPrice = (v) =>
  v == null
    ? null
    : Number(v).toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      })

function suggInitials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}
const agentDisplay = (a) =>
  [a.firstname, a.lastname].filter(Boolean).join(' ').trim() || a.email || '—'

async function fetchSuggestions(term) {
  const myReq = ++searchReqId
  searchLoading.value = true
  try {
    const resp = await apiFetch(`/api/private/search?q=${encodeURIComponent(term)}&limit=4`)
    const payload = await resp.json()
    if (myReq !== searchReqId) return
    searchData.value = payload?.data || { properties: [], contacts: [], agencies: [], agents: [] }
    searchCounts.value = payload?.counts || {
      properties: 0,
      contacts: 0,
      agencies: 0,
      agents: 0,
      total: 0,
    }
  } catch {
    if (myReq === searchReqId) {
      searchData.value = { properties: [], contacts: [], agencies: [], agents: [] }
      searchCounts.value = { properties: 0, contacts: 0, agencies: 0, agents: 0, total: 0 }
    }
  } finally {
    if (myReq === searchReqId) searchLoading.value = false
  }
}

function onSearchInput() {
  clearTimeout(searchTimer)
  const term = searchQuery.value.trim()
  if (term.length < 2) {
    searchOpen.value = term.length > 0
    searchData.value = { properties: [], contacts: [], agencies: [], agents: [] }
    searchCounts.value = { properties: 0, contacts: 0, agencies: 0, agents: 0, total: 0 }
    return
  }
  searchOpen.value = true
  searchTimer = setTimeout(() => fetchSuggestions(term), 250)
}

function onSearchFocus() {
  if (searchQuery.value.trim().length >= 1) searchOpen.value = true
}

function goToResults() {
  const term = searchQuery.value.trim()
  if (term.length < 2) return
  searchOpen.value = false
  router.push({ name: 'SearchResults', query: { q: term } })
}

function openSuggestion(path) {
  searchOpen.value = false
  searchQuery.value = ''
  router.push(path)
}

const handleLogout = async () => {
  closeUserMenu()
  try {
    await authStore.logout()
  } finally {
    // Toujours rediriger vers /login, y compris si la requête échoue
    // (les cookies HTTPOnly auront été marqués comme à supprimer).
    router.replace('/login')
  }
}

const toggleMobileSidebar = () => {
  layoutStore.toggleMobileSidebar()
}
</script>

<template>
  <header class="app-header">
    <div class="header-left">
      <!-- Bouton menu mobile -->
      <button
        v-if="layoutStore.isMobile"
        class="icon-btn mobile-menu-btn"
        @click="toggleMobileSidebar"
        aria-label="Ouvrir le menu"
      >
        <i class="pi pi-bars"></i>
      </button>
    </div>
    
    <div class="header-right">
      <!-- Recherche globale (omnibox) -->
      <div class="search-box" ref="searchBoxRef">
        <form class="search-form" @submit.prevent="goToResults">
          <div class="search-inputwrap">
            <i class="pi pi-search search-icon"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher un bien, contact, agence…"
              class="search-input"
              aria-label="Recherche globale"
              autocomplete="off"
              @input="onSearchInput"
              @focus="onSearchFocus"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear"
              aria-label="Effacer"
              @click="searchQuery = ''; searchOpen = false"
            >
              <i class="pi pi-times"></i>
            </button>
          </div>
          <button type="submit" class="search-go-btn" aria-label="Lancer la recherche">
            <i class="pi pi-search"></i>
          </button>
        </form>

        <!-- Dropdown suggestions -->
        <transition name="dropdown">
          <div v-if="searchOpen" class="search-dropdown" role="listbox">
            <!-- Chargement -->
            <div v-if="searchLoading && !hasSuggestions" class="search-state">
              <i class="pi pi-spin pi-spinner"></i> Recherche…
            </div>

            <!-- Aucun résultat -->
            <div
              v-else-if="!searchLoading && searchQuery.trim().length >= 2 && !hasSuggestions"
              class="search-state"
            >
              <i class="pi pi-search"></i> Aucun résultat pour « {{ searchQuery.trim() }} »
            </div>

            <!-- Saisir plus -->
            <div v-else-if="searchQuery.trim().length < 2" class="search-state search-state--hint">
              Saisissez au moins 2 caractères…
            </div>

            <template v-else>
              <!-- Biens -->
              <div v-if="searchData.properties.length" class="sugg-group">
                <div class="sugg-head">
                  <i class="pi pi-home"></i> Biens
                  <span class="sugg-head-count">{{ searchCounts.properties }}</span>
                </div>
                <button
                  v-for="p in searchData.properties"
                  :key="'p' + p.id"
                  class="sugg-item"
                  @click="openSuggestion(`/properties/${p.id}`)"
                >
                  <div class="sugg-thumb">
                    <img v-if="p.photo" :src="p.photo" :alt="p.title" />
                    <i v-else class="pi pi-home"></i>
                  </div>
                  <div class="sugg-text">
                    <span class="sugg-title">{{ p.title || 'Sans titre' }}</span>
                    <span class="sugg-sub">
                      <template v-if="p.reference">{{ p.reference }} · </template>
                      {{ [p.zipcode, p.city].filter(Boolean).join(' ') || '—' }}
                    </span>
                  </div>
                  <span v-if="p.price != null" class="sugg-meta">{{ formatPrice(p.price) }}</span>
                </button>
              </div>

              <!-- Contacts -->
              <div v-if="searchData.contacts.length" class="sugg-group">
                <div class="sugg-head">
                  <i class="pi pi-users"></i> Contacts
                  <span class="sugg-head-count">{{ searchCounts.contacts }}</span>
                </div>
                <button
                  v-for="c in searchData.contacts"
                  :key="'c' + c.id"
                  class="sugg-item"
                  @click="openSuggestion(`/contacts/${c.id}`)"
                >
                  <div class="sugg-avatar sugg-avatar--blue">{{ suggInitials(c.name) }}</div>
                  <div class="sugg-text">
                    <span class="sugg-title">{{ c.name }}</span>
                    <span class="sugg-sub">{{ c.phone || c.email || '—' }}</span>
                  </div>
                </button>
              </div>

              <!-- Agences -->
              <div v-if="isAdmin && searchData.agencies.length" class="sugg-group">
                <div class="sugg-head">
                  <i class="pi pi-building"></i> Agences
                  <span class="sugg-head-count">{{ searchCounts.agencies }}</span>
                </div>
                <button
                  v-for="a in searchData.agencies"
                  :key="'ag' + a.id"
                  class="sugg-item"
                  @click="openSuggestion(`/agences/${a.id}`)"
                >
                  <div class="sugg-avatar sugg-avatar--dark">
                    <img v-if="a.logo" :src="a.logo" :alt="a.name" />
                    <template v-else>{{ suggInitials(a.name) }}</template>
                  </div>
                  <div class="sugg-text">
                    <span class="sugg-title">{{ a.name }}</span>
                    <span class="sugg-sub">{{ [a.brand, a.city].filter(Boolean).join(' · ') || '—' }}</span>
                  </div>
                </button>
              </div>

              <!-- Agents -->
              <div v-if="isAdmin && searchData.agents.length" class="sugg-group">
                <div class="sugg-head">
                  <i class="pi pi-id-card"></i> Agents
                  <span class="sugg-head-count">{{ searchCounts.agents }}</span>
                </div>
                <button
                  v-for="a in searchData.agents"
                  :key="'at' + a.id"
                  class="sugg-item"
                  @click="openSuggestion(`/agents/${a.id}`)"
                >
                  <div class="sugg-avatar sugg-avatar--gold">
                    <img v-if="a.avatar" :src="a.avatar" :alt="agentDisplay(a)" />
                    <template v-else>{{ suggInitials(agentDisplay(a)) }}</template>
                  </div>
                  <div class="sugg-text">
                    <span class="sugg-title">{{ agentDisplay(a) }}</span>
                    <span class="sugg-sub">{{ a.email || '—' }}</span>
                  </div>
                </button>
              </div>

              <!-- Voir tous les résultats -->
              <button class="sugg-all" @click="goToResults">
                <i class="pi pi-arrow-right"></i>
                Voir tous les résultats ({{ searchCounts.total }})
              </button>
            </template>
          </div>
        </transition>
      </div>
      
      <!-- Notifications -->
      <div class="notif-menu" ref="notifRef">
        <button
          class="icon-btn notification-btn"
          :class="{ 'has-badge': unreadCount > 0 }"
          aria-label="Notifications"
          :aria-expanded="notifOpen"
          @click="toggleNotif"
        >
          <i class="pi pi-bell"></i>
          <span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>
        </button>

        <transition name="dropdown">
          <div v-if="notifOpen" class="notif-dropdown" role="menu">
            <div class="notif-head">
              <strong>Notifications</strong>
              <button v-if="unreadCount > 0" class="notif-mark-all" @click="markAllNotif">
                Tout marquer comme lu
              </button>
            </div>
            <div v-if="!notifications.length" class="notif-empty">
              Aucune notification
            </div>
            <ul v-else class="notif-list">
              <li
                v-for="n in notifications"
                :key="n.id"
                class="notif-item"
                :class="{ unread: !n.read }"
                @click="onNotifClick(n)"
              >
                <span class="notif-dot" :class="{ on: !n.read }"></span>
                <div class="notif-body">
                  <span class="notif-title">{{ n.title || 'Notification' }}</span>
                  <span v-if="n.message" class="notif-msg">{{ n.message }}</span>
                  <span class="notif-time">{{ notifTime(n.createdAt) }}</span>
                </div>
              </li>
            </ul>
          </div>
        </transition>
      </div>
      
      <!-- Menu utilisateur (toggle au clic, click-outside ferme) -->
      <div
        class="user-menu"
        :class="{ 'user-menu--open': userMenuOpen }"
        ref="userMenuRef"
      >
        <button
          class="user-btn"
          aria-label="Menu utilisateur"
          :aria-expanded="userMenuOpen"
          aria-haspopup="menu"
          @click="toggleUserMenu"
        >
          <div class="user-avatar">
            <img
              v-if="user?.avatarUrl"
              :src="user.avatarUrl"
              :alt="userInitials"
              class="avatar-img"
            />
            <span v-else class="avatar-text">{{ userInitials }}</span>
          </div>
          <div v-if="!layoutStore.isMobile" class="user-info">
            <span class="user-name">{{ user?.firstname }} {{ user?.lastname }}</span>
            <span class="user-role">{{ user?.role }}</span>
          </div>
          <i
            class="pi user-chevron"
            :class="userMenuOpen ? 'pi-angle-up' : 'pi-angle-down'"
          ></i>
        </button>

        <transition name="dropdown">
          <div v-if="userMenuOpen" class="user-dropdown" role="menu">
            <div class="dropdown-header">
              <span class="dropdown-email">{{ user?.email }}</span>
            </div>
            <div class="dropdown-divider"></div>
            <router-link to="/account" class="dropdown-item" @click="closeUserMenu">
              <i class="pi pi-user"></i>
              Mon compte
            </router-link>
            <router-link to="/account/settings" class="dropdown-item" @click="closeUserMenu">
              <i class="pi pi-cog"></i>
              Paramètres
            </router-link>
            <div class="dropdown-divider"></div>
            <button
              class="dropdown-item dropdown-item--danger"
              @click="handleLogout"
            >
              <i class="pi pi-sign-out"></i>
              Déconnexion
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-6);
  height: var(--header-height);
  background-color: var(--surface-card);
  border-bottom: 1px solid var(--surface-border);
  box-shadow: var(--shadow-xs);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.mobile-menu-btn {
  display: none;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-form {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.search-inputwrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 300px;
}

.search-icon {
  position: absolute;
  left: var(--space-3);
  color: var(--text-muted);
  font-size: var(--text-sm);
  pointer-events: none;
}

.search-go-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--primary-500);
  background: var(--primary-500);
  color: var(--text-inverse, #fff);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: background-color var(--transition-fast), border-color var(--transition-fast);
}
.search-go-btn:hover {
  background: var(--primary-600);
  border-color: var(--primary-600);
}
.search-go-btn i {
  font-size: var(--text-base);
}

.search-input {
  width: 100%;
  padding: var(--space-2) var(--space-8) var(--space-2) var(--space-8);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius-lg);
  background-color: var(--surface-ground);
  color: var(--text-primary);
  font-size: var(--text-sm);
  transition: all var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  background-color: var(--surface-card);
}

.search-input::placeholder {
  color: var(--text-muted);
}

.search-clear {
  position: absolute;
  right: var(--space-2);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: var(--border-radius-full);
  cursor: pointer;
  transition: all var(--transition-fast);
}
.search-clear:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}
.search-clear i {
  font-size: 11px;
}

/* ── Dropdown suggestions ─────────────────────────────────────── */
.search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 420px;
  max-width: min(420px, calc(100vw - 2rem));
  max-height: min(70vh, 560px);
  overflow-y: auto;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-dropdown);
  padding: var(--space-2);
}

.search-state {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  color: var(--text-secondary);
  font-size: var(--text-sm);
}
.search-state--hint {
  color: var(--text-muted);
}

.sugg-group {
  margin-bottom: var(--space-1);
}
.sugg-head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3) var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--font-bold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}
.sugg-head i {
  color: var(--primary-500);
}
.sugg-head-count {
  margin-left: auto;
  font-weight: var(--font-semibold);
  color: var(--text-muted);
}

.sugg-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  background: transparent;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  text-align: left;
  transition: background-color var(--transition-fast);
}
.sugg-item:hover {
  background: var(--surface-hover);
}

.sugg-thumb {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: var(--border-radius-md);
  background: var(--surface-ground);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--tertiary-400, #c9c0ab);
}
.sugg-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sugg-avatar {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xs);
  font-weight: var(--font-bold);
  color: #fff;
  overflow: hidden;
}
.sugg-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sugg-avatar--blue { background: linear-gradient(135deg, var(--info-400), var(--info-600)); }
.sugg-avatar--gold { background: linear-gradient(135deg, var(--primary-400), var(--primary-600)); }
.sugg-avatar--dark { background: linear-gradient(135deg, #4b4b4b, #212121); }

.sugg-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sugg-title {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sugg-sub {
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sugg-meta {
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  color: var(--primary-600);
}

.sugg-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 100%;
  margin-top: var(--space-1);
  padding: var(--space-3);
  border: none;
  border-top: 1px solid var(--surface-border);
  background: transparent;
  color: var(--primary-600);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  cursor: pointer;
  border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  transition: background-color var(--transition-fast);
}
.sugg-all:hover {
  background: var(--primary-50, #f7f2e7);
}

.icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background-color: transparent;
  color: var(--text-secondary);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.icon-btn:hover {
  background-color: var(--surface-hover);
  color: var(--text-primary);
}

.icon-btn i {
  font-size: var(--text-lg);
}

.notification-btn.has-badge {
  color: var(--primary-500);
}

.badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background-color: var(--danger-500);
  color: var(--text-inverse);
  font-size: 10px;
  font-weight: var(--font-bold);
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-menu {
  position: relative;
}

.user-btn {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: none;
  background-color: transparent;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.user-btn:hover {
  background-color: var(--surface-hover);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: var(--border-radius-full);
  background: linear-gradient(135deg, var(--primary-500), var(--primary-600));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-text {
  color: var(--text-inverse);
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.user-name {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-primary);
  line-height: 1.2;
}

.user-role {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: capitalize;
  line-height: 1.2;
}

.user-chevron {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.notif-menu { position: relative; }
.notif-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(360px, 92vw);
  max-height: 60vh;
  overflow-y: auto;
  background: white;
  border: 1px solid var(--tertiary-200);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-dropdown);
}
.notif-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--tertiary-100);
  position: sticky;
  top: 0;
  background: white;
}
.notif-mark-all {
  background: none;
  border: none;
  color: var(--primary-600);
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  cursor: pointer;
}
.notif-empty { padding: var(--space-5); text-align: center; color: var(--text-muted); font-size: var(--text-sm); }
.notif-list { list-style: none; margin: 0; padding: 0; }
.notif-item {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--tertiary-100);
  cursor: pointer;
}
.notif-item:hover { background: var(--tertiary-50); }
.notif-item.unread { background: var(--primary-50, #f9f1de); }
.notif-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; flex-shrink: 0; background: transparent; }
.notif-dot.on { background: var(--primary-500); }
.notif-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.notif-title { font-weight: var(--font-semibold); font-size: var(--text-sm); color: var(--text-primary); }
.notif-msg { font-size: var(--text-xs); color: var(--text-secondary); }
.notif-time { font-size: var(--text-xs); color: var(--text-muted); }

.user-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  min-width: 240px;
  z-index: var(--z-dropdown);
  padding: var(--space-1) 0;
}

.user-menu--open .user-btn {
  background-color: var(--surface-hover);
}

/* Transition d'ouverture du menu */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.dropdown-header {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--surface-border);
}

.dropdown-email {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  word-break: break-all;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--surface-border);
  margin: var(--space-1) 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  color: var(--text-primary);
  text-decoration: none;
  font-size: var(--text-sm);
  transition: background-color var(--transition-fast);
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.dropdown-item:hover {
  background-color: var(--surface-hover);
}

.dropdown-item--danger {
  color: var(--danger-600);
}

.dropdown-item--danger:hover {
  background-color: var(--danger-50);
}

.dropdown-item i {
  font-size: var(--text-sm);
  color: var(--text-muted);
  width: 20px;
  text-align: center;
}

/* Responsive */
@media (max-width: 1024px) {
  .mobile-menu-btn {
    display: flex;
  }
  
  .search-box {
    display: none;
  }
}

@media (max-width: 640px) {
  .app-header {
    padding: 0 var(--space-4);
  }
}
</style>
